
import { Destroyer, Generators, isNullOrUndefined } from '@adaptiva/core/common';
import { Summarizable } from '@adaptiva/core/summary';
import { UiService } from '@adaptiva/core/ui';
import { BaseEditorError } from '@adaptiva/data-service';
import { MetadataAuthoringProperties, MetadataObject, MetadataSerialization } from '@adaptiva/metadata/common';
import { AfterViewInit, Directive, EventEmitter, HostBinding, Input, Output } from '@angular/core';
import { isEqual } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { IAuthoringPropertyOutput } from '../interfaces';

@Directive()
export abstract class BaseAuthoringPropertiesComponent<T extends MetadataAuthoringProperties> extends Destroyer implements AfterViewInit {

	public abstract propertyConstructor: MetadataSerialization.Deserializable;

	public readonly propertyIsRequired: typeof MetadataSerialization.isRequired = MetadataSerialization.isRequired;

	public readonly propertyIsOptional: typeof MetadataSerialization.isOptional = MetadataSerialization.isOptional;

	public readonly getRequiredProperties: typeof MetadataSerialization.getRequiredProperties = MetadataSerialization.getRequiredProperties;

	@HostBinding('class.ada-container')
	public componentContainerClass: boolean = true;

	@HostBinding('class.ada-colspan-4')
	public componentColspanClass: boolean = true;

	@HostBinding('class.ada-col-1')
	public componentColClass: boolean = true;

	@HostBinding('style.padding')
	public componentPadding: string = '1.2rem';

	@Input()
	public isDisabled: boolean = false;

	@Input()
	public isInheritanceDisabled: boolean = false;

	@Input()
	public editorClass: typeof Summarizable;

	@Input()
	public inheritedPropertyObject?: T;

	@Input()
	public currentPropertyObject: T;

	@Input()
	public availableProperties: BehaviorSubject<string[]> = new BehaviorSubject([]);

	@Input()
	public forCustomization: boolean = false;

	@Input()
	public serverObject$: BehaviorSubject<MetadataObject>;

	@Output()
	public propertyObjectEmitter: EventEmitter<IAuthoringPropertyOutput<T>> = new EventEmitter();

	public guid: int = Generators.getIntId();

	public readonly defaultCardPadding: string = JSON.stringify(['1rem', '1rem', '1.25rem', '1rem']);

	constructor(public UI: UiService) {
		super();
	}

	public shouldDisplay(_property: string): boolean {
		return MetadataSerialization.isOptional(this.editorClass, this.propertyConstructor.namespace, _property)
			|| MetadataSerialization.isRequired(this.editorClass, this.propertyConstructor.namespace, _property);
	}

	public ngAfterViewInit(): void {
		if (this.currentPropertyObject && this.commitModelToView)
			this.commitModelToView();
	}

	public commitModelToView?(): void;

	public validate?(): BaseEditorError[];

	public createNewPropertyObject(): void {
		this.currentPropertyObject = new this.propertyConstructor();
	}

	public setObjectListOutput(_property: string, _objectIds: long | long[]): void {
		if (Array.isArray(_objectIds) && _objectIds?.length)
			this.setObjectPropertyValue(_property, _objectIds);
		else if (_objectIds)
			this.setObjectPropertyValue(_property, _objectIds);
		else
			this.deleteObjectPropertyValue(_property);
	}

	public setObjectPropertyValue(_propertyName: string, _value: any, _emit: boolean = true): void {
		if (this.currentPropertyObject) {
			const _inheritedProperty: any = this.inheritedPropertyObject?.[_propertyName];

			if (
				isNullOrUndefined(_value)
				|| (!!_inheritedProperty && isEqual(_value, _inheritedProperty))
			)
				delete this.currentPropertyObject[_propertyName];
			else
				this.currentPropertyObject[_propertyName] = _value;

			if (_emit)
				this._emitCurrentPropertyObject();
		}
	}

	public deleteObjectPropertyValue(propertyName: string);
	public deleteObjectPropertyValue(propertyName: string, then: Function);
	public deleteObjectPropertyValue(_propertyName: string, _then?: Function): void {
		if (this.currentPropertyObject) {
			delete this.currentPropertyObject[_propertyName];
			this._emitCurrentPropertyObject();
		}

		// Deletion may need to call another loading function in case change detection can't detect.
		if (_then)
			setTimeout(_then);
	}

	public setCurrentPropertyObject(_object: T): void {
		this.currentPropertyObject = _object;
		this._emitCurrentPropertyObject();
	}

	private _emitCurrentPropertyObject(): void {
		this.propertyObjectEmitter.emit({
			propertyObject: this.currentPropertyObject,
			propertyConstructor: this.propertyConstructor
		});
	}

}
